import * as React from 'react';
import styled, { css, DefaultTheme } from 'styled-components';
import { memoizeFunction } from '@agilepoint/utils-common';
import { IBaseComponentProps } from '../../types';
import { INumericBoxProps, SourceOfChange } from '../numeric-box-props';
import { Icon } from '../../icon';
import { NumberboxState } from '../theme';

interface ISpinnerProps extends Pick<INumericBoxProps, 'localization' | 'disabled' | 'error'>, IBaseComponentProps {
    changeValue(isIncrement: boolean, sourceOfChange: SourceOfChange): void;
}

const getNumberboxStylesByState = memoizeFunction((state: NumberboxState) => {
    const getStyles = (theme: DefaultTheme) => {
        const { numberbox: { colors } } = theme;
        return colors[state];
    };
    return css`
        background-color: ${({ theme }) => getStyles(theme).iconBackgroundColor};
        .ap-icon {
            transition: fill .1s ease-in-out;
            fill: ${({ theme }) => getStyles(theme).iconColor};
        };
    `;
});

const commonStyles = css<INumericBoxProps>`
    width: 100%;
    height: 50%;
    justify-content: center;
    display: flex;
    align-items: center;
    transition: background-color .3s ease-in-out;
    ${({ error, disabled }) => (disabled ? getNumberboxStylesByState('disabled') : (getNumberboxStylesByState(error ? 'error' : 'default')))};
    :hover {
        ${({ error, disabled }) => (disabled ? getNumberboxStylesByState('disabled') : (getNumberboxStylesByState(error ? 'error' : 'hovered')))};
        
    }
    :active {
        ${({ error, disabled }) => (disabled ? getNumberboxStylesByState('disabled') : (getNumberboxStylesByState(error ? 'error' : 'active')))};
    }
`;

const IncrementerContainer = styled.div.attrs(() => ({
    className: String('ap-numericBox-incrementButton')
}))`
    ${commonStyles};
    border-radius: ${({ theme: { numberbox: { composites: { incrementorBorderRadius } } } }) => incrementorBorderRadius};
`;

const DecrementerContainer = styled.div.attrs(() => ({
    className: String('ap-numericBox-decrementButton')
}))`
    ${commonStyles};
    border-radius: ${({ theme: { numberbox: { composites: { decrementerBorderRadius } } } }) => decrementerBorderRadius};
`;

function SpinnerBase(props: ISpinnerProps) {
    const { className, changeValue, localization = {}, disabled, error } = props;

    const onIncrement = React.useCallback(() => {
        changeValue(true, 'spinner');
    }, [changeValue]);

    const onDecrement = React.useCallback(() => {
        changeValue(false, 'spinner');
    }, [changeValue]);

    return (
        <div className={className}>
            <IncrementerContainer title={localization.incrementerTitle} disabled={disabled} onClick={onIncrement} error={error}>
                <Icon iconName="ap-up-arrow-regular.outline" iconSize="12" />
            </IncrementerContainer>
            <DecrementerContainer title={localization.decrementerTitle} disabled={disabled} onClick={onDecrement} error={error}>
                <Icon iconName="ap-down-arrow-regular.outline" iconSize="12" />
            </DecrementerContainer>
        </div>
    );
}

export const Spinner = styled(SpinnerBase).attrs(() => ({
    className: String('ap-numberboxSpinner')
}))`
    position: absolute;
    display: inline-block;
    box-sizing: border-box;
    top: 1px;
    right: 1px;
    height: calc(100% - 2px);
    width: 38px;
    display: inline-block;
    box-sizing: border-box;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    z-index: 1;
    user-select: none;
`;
